import { useInput } from "@redotech/react-util/form";
import { useHandler } from "@redotech/react-util/hook";
import { DropoffProviderType } from "@redotech/redo-return-core-common/dropoff-provider";
import { Flex } from "@redotech/redo-web/flex";
import * as gridCss from "@redotech/redo-web/grid.module.css";
import { PageForm, PageFormSave } from "@redotech/redo-web/page-form";
import { Text } from "@redotech/redo-web/text";
import classNames from "classnames";
import { memo } from "react";
import * as teamCss from "../../team.module.css";
import {
  boxlessDropoffsForm,
  BoxlessDropoffsSettings,
  BoxlessDropoffsValue,
} from "./boxless-dropoffs-settings";
import { useBoxlessDropoffs } from "./use-boxless-dropoffs";

export const BoxlessDropoffsPage = memo(function BoxlessDropoffsPage() {
  const { initial, loaded, error, updateFee, refresh } = useBoxlessDropoffs();

  if (error) {
    return (
      <Flex>
        <Text textColor="error">{`Something went wrong loading Boxless Drop-offs settings: ${error instanceof Error ? error.message : String(error)}`}</Text>
      </Flex>
    );
  }

  if (!loaded) {
    return null;
  }

  return (
    <BoxlessDropoffsForm
      initial={initial}
      refresh={refresh}
      updateFee={updateFee}
    />
  );
});

const BoxlessDropoffsForm = memo(function BoxlessDropoffsForm({
  initial,
  updateFee,
  refresh,
}: {
  initial: BoxlessDropoffsValue;
  updateFee(
    provider: DropoffProviderType,
    fee: number,
    signal: AbortSignal,
  ): Promise<void>;
  refresh(): void;
}) {
  const formInput = useInput(boxlessDropoffsForm, initial);

  const save: PageFormSave = useHandler(async (signal) => {
    for (const feeInput of formInput.inputs) {
      if (!feeInput.changed) {
        continue;
      }
      await updateFee(
        feeInput.value.provider,
        Number(feeInput.value.fee),
        signal,
      );
    }
    // Snap fee strings to the canonical form the reload returns, so the form is clean post-save.
    formInput.setValue(
      formInput.value.map((entry) => ({
        ...entry,
        fee: String(Number(entry.fee)),
      })),
    );
    refresh();
  });

  return (
    <div className={teamCss.container}>
      <PageForm initial={initial} input={formInput} save={save}>
        <div className={classNames(teamCss.mainForm, gridCss.grid)}>
          <div className={gridCss.span12}>
            <BoxlessDropoffsSettings input={formInput} />
          </div>
        </div>
      </PageForm>
    </div>
  );
});
